import { orderBy } from 'lodash-es'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import type { ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { type Document } from '@/features/documents/Document'
import { type DocumentStore } from '@/features/documents/DocumentStore'
import { type Job } from '@/features/jobs/Job'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'

/**
 * Options for the {@link DocumentPickerViewModel}.
 */
export interface DocumentPickerViewModelOptions {
  /**
   * The job the picked documents are for. Only the current member's
   * documents in the job's workspace are offered.
   */
  job: Job

  /**
   * When given, a single document is picked for it instead of linking any
   * number of documents: documents already linked to the job are offered,
   * and the picker closes once the picked document is linked.
   */
  pick?: DocumentPick
}

/**
 * Picks a single document, tracking the pick outside the view model.
 */
export interface DocumentPick {
  /**
   * Whether the document is the picked one. It then stays listed
   * regardless of the other rules.
   */
  isPicked(doc: Document): boolean

  /**
   * Called when the document is picked, before it is linked to the job.
   */
  onPick(doc: Document): unknown
}

/**
 * Document picker view model.
 */
export class DocumentPickerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _documentVms

  readonly modal = new ModalViewModel()

  @observable accessor searchText = ''

  readonly scrollProvider = new InfiniteScrollProvider<Document>({
    limit: 30,
    fetchItems: ({ paging, signal }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Loading documents failed',
        signal,
      )

      return withRetry(() =>
        this._documentStore.searchDocuments(
          {
            context: 'User',
            workspaceId: Id.assert(this._options.job.workspace.id),
            ...paging,
          },
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _options: DocumentPickerViewModelOptions,
    private readonly _documentStore: DocumentStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._documentVms = new ViewModelList({
      models: () =>
        orderBy(
          this.scrollProvider.items.filter(
            (d) => this._canPick(d) && this._matchesSearchText(d),
          ),
          'dateCreated',
          'desc',
        ),
      create: (document) =>
        new DocumentPickerItemViewModel(
          document,
          this._options,
          this._documentStore,
          this._createAlertErrorHandler,
          this.modal.close,
        ),
    })

    this._lifecycle.manage(this._documentVms)
    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.scrollProvider,
        additions$: this._documentStore.documentAdded$,
        removals$: this._documentStore.documentRemoved$,
      }),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The documents that can be picked and match the search text.
   */
  @computed
  get documents() {
    return this._documentVms.items
  }

  /**
   * Shows the picker and loads the documents.
   */
  @action.bound
  show() {
    this.searchText = ''
    this.modal.show()
    // The picker renders a failed load from the task's own state.
    void this.fetch().catch(() => undefined)
  }

  /**
   * Loads the first page of documents.
   */
  fetch = task(async () => {
    await this.scrollProvider.fetch({
      signal: this._lifecycle.signal,
      staleWhileRevalidate: true,
    })
  })

  @action.bound
  setSearchText(value: string) {
    this.searchText = value
  }

  /**
   * Whether the document can be linked to the job.
   */
  private _canPick(doc: Document) {
    const { job, pick } = this._options
    if (doc.ephemeral) {
      return false
    }
    if (pick?.isPicked(doc)) {
      return true
    }
    if (doc.archived) {
      return false
    }
    if (!pick && doc.isLinkedToJob(job.id)) {
      return false
    }
    return doc.workspaceId === job.workspace.id
  }

  private _matchesSearchText(d: Document) {
    const lowered = this.searchText.toLowerCase().trim()
    if (lowered.length === 0) return true
    return Boolean(
      d.title.toLowerCase().includes(lowered) ||
      d.description.toLowerCase().includes(lowered),
    )
  }
}

/**
 * Item view model for the picker.
 */
export class DocumentPickerItemViewModel {
  constructor(
    readonly document: Document,
    private readonly _options: DocumentPickerViewModelOptions,
    private readonly _documentStore: DocumentStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _closePicker: () => void,
  ) {}

  @computed
  get fileName() {
    return this.document.title || ''
  }

  /**
   * Whether the document is picked, or linked to the job when linking any
   * number of documents.
   */
  @computed
  get isSelected() {
    const { job, pick } = this._options
    return pick
      ? pick.isPicked(this.document)
      : this.document.isLinkedToJob(job.id) && !this.document.ephemeral
  }

  /**
   * Picks the document, alerting when that fails. A single pick closes the
   * picker once the document is linked.
   */
  select = task.resolved(async () => {
    const { job, pick } = this._options
    pick?.onPick(this.document)
    const linked = await this._documentStore
      .createDocumentLink(this.document, {
        type: 'Job',
        jobId: Id.assert(job.id),
      })
      .then(() => true)
      .catch(this._createAlertErrorHandler('Linking the document failed'))
      .catch(() => false)
    if (linked && pick) {
      this._closePicker()
    }
  })
}
